import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Slider } from 'components/Slider';
import { Input } from 'components/Input';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import { baseTheme } from 'styles/utils';
import { cx } from '@linaria/core';
import { ThresholdSliderProps } from '../types';
import { container, input, sxStyles } from './styles';

const DEFAULT_LOWER_VALUE = 0;
const DEFAULT_UPPER_VALUE = 100;

export const ThresholdSlider: FC<ThresholdSliderProps> = ({
  className,
  defaultValue = [DEFAULT_LOWER_VALUE, DEFAULT_UPPER_VALUE],
  value,
  min = DEFAULT_LOWER_VALUE,
  max = DEFAULT_UPPER_VALUE,
  trailColor = baseTheme.light.colors.gray200,
  primaryColor = baseTheme.light.colors.warning500,
  secondaryColor = baseTheme.light.colors.success500,
  step = 1,
  onChange,
  ...slideProps
}) => {
  const [currentValue, setCurrentValue] = useState<string[]>(defaultValue.map(String));

  useEffect(() => {
    if (value && !isEmpty(value) && !isEqual(value, currentValue)) {
      setCurrentValue(value.map(String));
    }
  }, [value]);

  const handleChange = useCallback(
    (val: number[]) => {
      if (val[0] >= min && val[1] <= max && val[0] < val[1]) {
        setCurrentValue(val.map(String));
        onChange?.(val);
      }
    },
    [onChange, min, max],
  );

  const handleLowerInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;

      if (!val.startsWith('-')) {
        setCurrentValue([val, currentValue[1]]);
        onChange?.([+val, +currentValue[1]]);
      }
    },
    [currentValue, onChange],
  );

  const handleUpperInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCurrentValue([currentValue[0], event.target.value]);
      onChange?.([+currentValue[0], +event.target.value]);
    },
    [currentValue, onChange],
  );

  const handleLowerInputBlur = useCallback(() => {
    const currentLowerValue = +currentValue[0];
    const currentUpperValue = +currentValue[1];

    if (currentValue[0] === '' || currentLowerValue < min) {
      setCurrentValue([`${min}`, currentValue[1]]);
    } else if (currentLowerValue > currentUpperValue) {
      setCurrentValue([`${currentUpperValue - 1}`, currentValue[1]]);
    } // Otherwise the value is correct, so we leave it as is.
  }, [min, currentValue]);

  const handleUpperInputBlur = useCallback(() => {
    const currentLowerValue = +currentValue[0];
    const currentUpperValue = +currentValue[1];

    if (currentValue[1] === '' || currentUpperValue > max) {
      setCurrentValue([currentValue[0], `${max}`]);
    } else if (currentUpperValue < currentLowerValue) {
      setCurrentValue([currentValue[0], `${currentLowerValue + 1}`]);
    } // Otherwise the value is correct, so we leave it as is.
  }, [currentValue, max]);

  return (
    <div className={cx(container, className)}>
      <Input
        type="number"
        className={input}
        step={step || 1}
        lang="en"
        onChange={handleLowerInputChange}
        onBlur={handleLowerInputBlur}
        value={currentValue[0]}
      />
      <Slider
        {...slideProps}
        disableSwap
        sx={sxStyles(currentValue, trailColor, primaryColor, secondaryColor, max)}
        withLabels={false}
        min={min}
        max={max}
        step={step}
        value={currentValue.map(Number)}
        onChange={handleChange}
      />
      <Input
        type="number"
        className={input}
        step={step || 1}
        lang="en-US"
        onChange={handleUpperInputChange}
        onBlur={handleUpperInputBlur}
        value={currentValue[1]}
      />
    </div>
  );
};
